// alternative: https://github.com/krasimir/navigo
// route matching documentation: https://github.com/pillarjs/path-to-regexp
// possible enhancement: https://github.com/ReactTraining/history

import { match as make_matcher } from 'path-to-regexp';
import Events from './events';
import session from './session';

// module global variables -------------------------------------------------- //

// example_route1 = { path: '/example1', view: () => {} }
// example_route2 = { path: '/example2', view: () => {} }
// routes = [example_route1, example_route2, etc...]
let routes = [];

// helper functions --------------------------------------------------------- //

const get_query = search_str => {
  let query;

  if (search_str.startsWith('?next=')) {
    query = { next: search_str.replace('?next=', '') };
  } else {
    query = Object.fromEntries(new URLSearchParams(search_str));
  }

  return {
    query: query,
  };
};

const get_element_tree = (start_el, stop_el = document.body) => {
  let parents = [];
  let parent = start_el;

  while (parent && parent !== stop_el) {
    let other = parent;
    parents.push(other);
    parent = other.parentNode;
  }

  parents.push(stop_el);

  return parents;
};

const sort_routes = routes => {
  return [...routes].sort((r1, r2) => (r1.path >= r2.path ? -1 : +1));
};

const find_route_match = (routes, location) => {
  const matches = routes.map(route => {
    const match_fn = make_matcher(route.path);
    const result = match_fn(location.pathname);

    return {
      route: route,
      result: result,
    };
  });

  return matches.find(m => m.result);
};

const find_route = (routes, location) => {
  const sorted_routes = sort_routes(routes);
  let match = find_route_match(sorted_routes, location);

  if (!match) {
    match = {
      route: routes[routes.length - 1],
      result: [location.pathname],
    };
  }

  return match;
};

const get_view_options = (route, location) => {
  return {
    route: route.route.path,
    path: location.pathname,
    params: route.result.params ? route.result.params : {},
    ...get_query(location.search),
  };
};

// navigation --------------------------------------------------------------- //

const render_view = (routes, location) => {
  const match = find_route(routes, location);
  const view_options = get_view_options(match, location);

  match.route.view(view_options);
  return view_options;
};

const start_internal_navigation = elem => {
  if (elem && elem.dataset && 'link' in elem.dataset) {
    event.preventDefault();
    return navigate(elem.href.replace(window.location.origin, ''));
  }
};

const navigate = url => {
  if (url === 'javascript:;') {
    return;
  }
  Events.dispatch('wp:router:beforepathchange');
  history.pushState({}, null, url);
  Events.dispatch('wp:router:afterpathchange');

  Events.dispatch('wp:router:beforenavigation', {
    path: location.pathname,
  });
  const result = render_view(routes, location);

  Events.dispatch('wp:router:afternavigation', { path: result.path });
  session.write({ route: result.path });
  return result;
};

// adding and removing routes ----------------------------------------------- //

const add_route = route => {
  if (!routes.find(r => r.path === route.path)) {
    routes = [...routes, route];
  }
};

const remove_route = route => {
  routes.splice(
    routes.findIndex(r => r.path === route.path),
    1
  );
};

const add_routes = new_routes => {
  for (const route of new_routes) {
    add_route(route);
  }
};

const remove_routes = routes => {
  for (const route of routes) {
    remove_route(route);
  }
};

// event handlers ----------------------------------------------------------- //

const link_click_handler = event => {
  if (event.target.tagName === 'BUTTON') {
    return;
  } else if (event.target.tagName === 'A') {
    start_internal_navigation(event.target);
  } else {
    const elems = get_element_tree(event.target);

    for (const elem of elems) {
      start_internal_navigation(elem);
    }
  }
};

const url_change_handler = () => {
  let result;

  Events.dispatch('wp:router:beforenavigation', {
    path: location.pathname,
  });
  result = render_view(routes, location);
  Events.dispatch('wp:router:afternavigation', {
    path: location.pathname,
  });

  return result;
};

// lifecycle functions ------------------------------------------------------ //

const stop = () => {
  document.body.removeEventListener('click', link_click_handler);
  window.removeEventListener('popstate', url_change_handler);
  window.removeEventListener('beforeunload', stop);
};

const start = () => {
  window.addEventListener('beforeunload', stop);
  window.addEventListener('popstate', url_change_handler);
  document.body.addEventListener('click', link_click_handler);

  navigate(location);

  return stop;
};

// exports ------------------------------------------------------------------ //

export default {
  routes,
  start,
  stop,
  add_route,
  remove_route,
  add_routes,
  remove_routes,
  navigate,
};
